(() => {
  if (window.__countryAvailabilityPopupLoaded) return;
  window.__countryAvailabilityPopupLoaded = true;

  const previousFocus = new WeakMap();
  const SELECTOR = '[data-country-availability-popup]';
  const VISIBLE_CLASS = 'is-visible';
  const OPEN_CLASS = 'country-availability-popup-open';
  const SESSION_DISMISSAL_KEY = 'zwick_country_popup_dismissed';
  const ERP_SESSION_DISMISSAL_KEY = 'zwick_erp_popup_dismissed';
  let dismissedInDocument = false;

  const normalizeCode = (value) => String(value || '').trim().toUpperCase();

  const parseAllowedCountries = (popup) => new Set(
    String(popup.dataset.liveCountryCodes || '')
      .split(',')
      .map(normalizeCode)
      .filter(Boolean)
  );

  const isDismissed = (popup) => {
    if (dismissedInDocument) return true;
    try {
      if (window.sessionStorage.getItem(SESSION_DISMISSAL_KEY) === 'true') return true;
      if (popup.dataset.dismissalKey && window.sessionStorage.getItem(popup.dataset.dismissalKey) === 'true') return true;
    } catch (error) {}
    try {
      return document.cookie.split(';').some((cookie) => cookie.trim() === `${SESSION_DISMISSAL_KEY}=true`);
    } catch (error) {
      return false;
    }
  };

  const rememberDismissal = (popup) => {
    dismissedInDocument = true;
    try {
      window.sessionStorage.setItem(SESSION_DISMISSAL_KEY, 'true');
      if (popup.dataset.dismissalKey) window.sessionStorage.setItem(popup.dataset.dismissalKey, 'true');
    } catch (error) {}
    try {
      const secure = window.location?.protocol === 'https:' ? '; Secure' : '';
      document.cookie = `${SESSION_DISMISSAL_KEY}=true; Path=/; SameSite=Lax${secure}`;
    } catch (error) {}
  };

  const focusableElements = (popup) => Array.from(
    popup.querySelectorAll(
      'a[href], button:not([disabled]):not([tabindex="-1"]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
    )
  ).filter((element) => element instanceof HTMLElement && element.offsetParent !== null);

  const setPageScrollLock = (locked) => {
    document.documentElement.classList.toggle(OPEN_CLASS, locked);
    document.body.classList.toggle(OPEN_CLASS, locked);
  };

  // If both notices are applicable, wait for the ERP notice to be closed
  // before displaying this country notice. This also handles asynchronous
  // country detection finishing before or after the ERP dismissal.
  const erpNoticeIsPending = () => {
    const erp = document.querySelector('[data-erp-transition-popup]');
    if (!erp || erp.dataset.erpPopupComplete === 'true') return false;
    // Do not open the country notice while the ERP notice is still fading out.
    if (erp.dataset.erpPopupClosing === 'true') return true;
    try {
      if (window.sessionStorage.getItem(ERP_SESSION_DISMISSAL_KEY) === 'true') return false;
      if (erp.dataset.dismissalKey && window.sessionStorage.getItem(erp.dataset.dismissalKey) === 'true') return false;
    } catch (error) {}
    try {
      if (document.cookie.split(';').some((cookie) => cookie.trim() === `${ERP_SESSION_DISMISSAL_KEY}=true`)) return false;
    } catch (error) {}
    return true;
  };

  const showPopup = (popup) => {
    if (!popup.hidden || isDismissed(popup)) return;

    if (erpNoticeIsPending()) {
      if (popup.dataset.countryWaitingForErp !== 'true') {
        popup.dataset.countryWaitingForErp = 'true';
        document.addEventListener('zwick:erp-popup-dismissed', () => {
          delete popup.dataset.countryWaitingForErp;
          if (popup.isConnected) showPopup(popup);
        }, { once: true });
      }
      return;
    }

    if (document.activeElement instanceof HTMLElement) previousFocus.set(popup, document.activeElement);

    popup.hidden = false;
    popup.setAttribute('aria-hidden', 'false');
    setPageScrollLock(true);

    window.requestAnimationFrame(() => {
      popup.classList.add(VISIBLE_CLASS);
      const focusTarget = focusableElements(popup)[0];
      if (focusTarget) focusTarget.focus({ preventScroll: true });
    });
  };

  const hidePopup = (popup, remember = true) => {
    if (popup.hidden) return;
    if (remember) rememberDismissal(popup);

    popup.classList.remove(VISIBLE_CLASS);
    popup.setAttribute('aria-hidden', 'true');
    setPageScrollLock(false);

    const finish = () => {
      popup.hidden = true;
      const previous = previousFocus.get(popup);
      if (previous && document.contains(previous)) previous.focus({ preventScroll: true });
      previousFocus.delete(popup);
    };

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      finish();
      return;
    }

    window.setTimeout(finish, 200);
  };

  const countryFromResponse = (data) => normalizeCode(
    data && data.detected_values && data.detected_values.country && data.detected_values.country.handle
  );

  const requestDetectedCountry = async () => {
    const routeRoot = window.Shopify?.routes?.root || '/';
    const currentCountry = normalizeCode(window.Shopify?.country);
    const queries = [
      'country[enabled]=true',
      currentCountry
        ? `country[enabled]=true&country[exclude]=${encodeURIComponent(currentCountry)}`
        : 'country[enabled]=true'
    ];

    const paths = [];
    queries.forEach((query) => {
      paths.push(`${routeRoot}browsing_context_suggestions.json?${query}`);
      paths.push(`/browsing_context_suggestions.json?${query}`);
    });

    for (const path of [...new Set(paths)]) {
      try {
        const response = await fetch(path, {
          method: 'GET',
          headers: { Accept: 'application/json' },
          credentials: 'same-origin',
          cache: 'no-store'
        });

        if (!response.ok) continue;

        const country = countryFromResponse(await response.json());
        if (country) return country;
      } catch (error) {}
    }

    return '';
  };

  const bindPopupEvents = (popup) => {
    popup.querySelectorAll('[data-country-popup-close]').forEach((element) => {
      element.addEventListener('click', () => hidePopup(popup));
    });

    popup.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        hidePopup(popup);
        return;
      }

      if (event.key !== 'Tab') return;

      const focusable = focusableElements(popup);
      if (!focusable.length) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    });
  };

  const initializePopup = async (popup) => {
    if (!(popup instanceof HTMLElement) || popup.dataset.countryPopupInitialized === 'true') return;

    popup.dataset.countryPopupInitialized = 'true';
    bindPopupEvents(popup);

    const designMode = popup.dataset.designMode === 'true';
    const previewInEditor = popup.dataset.previewInEditor === 'true';

    if (designMode && previewInEditor) {
      showPopup(popup);
      return;
    }

    if (isDismissed(popup)) return;

    const allowedCountries = parseAllowedCountries(popup);
    const detectedCountry = await requestDetectedCountry();
    const fallbackCountry = normalizeCode(popup.dataset.fallbackCountry || window.Shopify?.country);
    const countryToCheck = detectedCountry || fallbackCountry;

    if (countryToCheck && !allowedCountries.has(countryToCheck)) {
      showPopup(popup);
    }
  };

  const initializeAll = (root = document) => {
    root.querySelectorAll(SELECTOR).forEach(initializePopup);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initializeAll(), { once: true });
  } else {
    initializeAll();
  }

  document.addEventListener('shopify:section:load', (event) => {
    if (event.target instanceof Element) initializeAll(event.target);
  });

  document.addEventListener('shopify:section:select', (event) => {
    const popup = event.target instanceof Element ? event.target.querySelector(SELECTOR) : null;
    if (popup && popup.dataset.previewInEditor === 'true') showPopup(popup);
  });
})();
